/**
 * RiskPage - Budget/Forecast Risk Flagging
 *
 * Shows accounts whose planned value is outside expected ranges. Three
 * comparison modes:
 *   - TTM:            plan vs the trailing 12 months of actuals (mean, min–max range)
 *   - vs LY Budget:   plan deviates > threshold% from last year's budget.
 *                     Two scopes: a single month, or the full year with
 *                     month-by-month deltas (the default for Budget versions —
 *                     the core budget-review workflow).
 *   - vs Prior Months: plan deviates > threshold% from the latest complete
 *                     month's actuals, or from the average of any selected
 *                     months in the TTM window
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShieldAlert,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  CheckCircle2,
  HelpCircle,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { useForecastV3, useMonthlyVersions } from '../../context/ForecastV3Context';
import { calculateForecast, fetchForecastPnLData, type CalculationResult } from '../../api';
import { useForecastData, forecastQueryKeys, latestClosedMonth, effectiveFinancialCloseDay } from '../../hooks/useForecastData';
import { defaultMonthForYear } from '../../utils';
import { AccountDetailPanel } from '../workspace/AccountDetailPanel';
import type { ForecastAccount, DriverType } from '../../types';
// @ts-ignore
import { api } from '../../../../utils/api';
import { formatCurrencyRounded } from '../../../../utils/currency';
// @ts-ignore
import API_ENDPOINTS from '../../../../config/apiConfig';

// ============ Types ============

type ComparisonMode = 'ttm' | 'lyBudget' | 'months';
type BudgetScope = 'month' | 'year';
type SortField = 'accountName' | 'department' | 'forecast' | 'comparison' | 'varianceAmt' | 'variancePct';
type SortDir = 'asc' | 'desc';
type FlagStatus = 'over' | 'under' | 'ok' | 'noData';

interface RiskAccountRecord {
  chartOfAccountId: number;
  historicalMean: number | null;
  historicalStdDev: number | null;
  historicalMin: number | null;
  historicalMax: number | null;
  historicalDataPoints: number | null;
}

interface RiskMonthlyRecord {
  chartOfAccountId: number;
  yr: number;
  mo: number;
  monthlyAmount: number;
}

interface RiskLyBudgetRecord {
  chartOfAccountId: number;
  mo: number;
  budgetAmount: number;
}

interface RiskRow {
  chartOfAccountId: number;
  accountName: string;
  accountNumber: string;
  department: string;
  accountType: string;
  forecast: number | null;
  // TTM
  historicalMean: number | null;
  historicalStdDev: number | null;
  historicalMin: number | null;
  historicalMax: number | null;
  historicalDataPoints: number | null;
  // Computed
  comparisonValue: number | null;
  varianceAmt: number | null;
  variancePct: number | null;
  flagStatus: FlagStatus;
  // Full-year scope: per-month current values, LY budget, and deltas (Jan–Dec)
  monthlyCur?: number[];
  monthlyLy?: (number | null)[];
  monthlyDeltas?: number[];
}

// ============ Constants ============

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const SHORT_MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// Minimum dollar materiality — ignore sub-$1 variances across all modes
const MATERIAL_THRESHOLD = 1;
// Full-year scope: a single month's delta must clear this floor (in addition
// to the threshold %) before it alone flags an account
const MONTH_DELTA_FLOOR = 100;

// ============ Helpers ============

function fmtCurrency(value: number | null, currency?: string | null): string {
  if (value === null || value === undefined) return '—';
  return formatCurrencyRounded(value, currency);
}

function fmtPct(value: number | null): string {
  if (value === null || value === undefined) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

function fmtDelta(value: number): string {
  const formatted = formatCurrencyRounded(Math.abs(value));
  return value >= 0 ? `+${formatted}` : `-${formatted}`;
}

function monthKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

// ============ Sort Header ============

function SortHeader({
  field,
  label,
  sortField,
  sortDir,
  onSort,
  align = 'left',
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
  align?: 'left' | 'right';
}) {
  const active = sortField === field;
  return (
    <th
      className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide cursor-pointer select-none whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'}`}
      style={{ color: active ? 'var(--lodge-orange-500)' : 'var(--lodge-onyx-400)' }}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {align === 'right' && (
          active
            ? (sortDir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />)
            : <ChevronsUpDown size={12} style={{ opacity: 0.4 }} />
        )}
        {label}
        {align === 'left' && (
          active
            ? (sortDir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />)
            : <ChevronsUpDown size={12} style={{ opacity: 0.4 }} />
        )}
      </span>
    </th>
  );
}

// ============ Main Component ============

export function RiskPage() {
  const { state } = useForecastV3();
  const { orgId, locationId, year, currentVersion } = state;
  const { activeMonth } = useMonthlyVersions();

  const isBudget = currentVersion?.type === 'Budget';
  const kindLabel = isBudget ? 'Budget' : 'Forecast';
  const kindLower = isBudget ? 'budget' : 'forecast';

  // Use the active month from the top header; fall back to the year's default month
  const selectedMonth = activeMonth ?? defaultMonthForYear(year ?? new Date().getFullYear());
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('ttm');
  const [budgetScope, setBudgetScope] = useState<BudgetScope>('month');
  const [threshold, setThreshold] = useState<number>(5);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState<boolean>(true);
  const [sortField, setSortField] = useState<SortField>('variancePct');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterType, setFilterType] = useState<'all' | 'Revenue' | 'Expense'>('all');

  // Budget review defaults to the full-year budget-vs-LY-budget comparison;
  // forecasts default to TTM. Re-applies when the working version changes.
  useEffect(() => {
    if (!currentVersion) return;
    if (currentVersion.type === 'Budget') {
      setComparisonMode('lyBudget');
      setBudgetScope('year');
    } else {
      setComparisonMode('ttm');
      setBudgetScope('month');
    }
  }, [currentVersion?.id, currentVersion?.type]);

  const isYearScope = (comparisonMode === 'lyBudget' || comparisonMode === 'ttm') && budgetScope === 'year';

  // Months (yyyy-mm keys) chosen for the "vs Prior Months" comparison.
  // Empty = default to the latest complete month.
  const [selectedCompareMonths, setSelectedCompareMonths] = useState<string[]>([]);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const monthDropdownRef = useRef<HTMLDivElement | null>(null);

  const versionId = currentVersion?.id ?? null;
  const enabled = !!orgId && !!locationId && !!year && !!versionId;

  const queryClient = useQueryClient();

  // The TTM window: the 12 months immediately before the selected month,
  // oldest first. These are also the selectable comparison months.
  const windowMonths = useMemo(() => {
    if (!year) return [];
    const months: { y: number; m: number; key: string; label: string }[] = [];
    for (let i = 12; i >= 1; i--) {
      const d = new Date(year, selectedMonth - 1 - i, 1);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      months.push({ y, m, key: monthKey(y, m), label: `${SHORT_MONTH_NAMES[m - 1]} ${y}` });
    }
    return months;
  }, [year, selectedMonth]);

  // Selected compare months reference the window — clear them when it moves
  useEffect(() => {
    setSelectedCompareMonths([]);
  }, [year, selectedMonth]);

  // Close the month dropdown on outside click
  useEffect(() => {
    if (!showMonthDropdown) return;
    const handleClick = (e: MouseEvent) => {
      if (monthDropdownRef.current && !monthDropdownRef.current.contains(e.target as Node)) {
        setShowMonthDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMonthDropdown]);

  // ---- Detail panel state ----
  const [panelAccountId, setPanelAccountId] = useState<number | null>(null);

  // ---- Fetch full forecast accounts (detail panel + full-year comparison) ----
  const {
    accounts: forecastAccounts,
    updateDriver,
    saveValue,
    clearMonth: clearMonthForecast,
    isLoading: isAccountDataLoading,
    isRefetching: isPanelRefetching,
    financialCloseDay,
  } = useForecastData({
    orgId: orgId ?? 0,
    locationId: locationId ?? 0,
    year: year ?? new Date().getFullYear(),
    versionId,
    enabled: !!orgId && !!locationId && !!year && !!versionId,
  });

  const accountsById = useMemo(() => {
    const map = new Map<number, ForecastAccount>();
    for (const acc of forecastAccounts) {
      map.set(acc.chartOfAccountId, acc);
    }
    return map;
  }, [forecastAccounts]);

  // Location close-day anchor: the most recent calendar month whose books are
  // closed (month m closes on day `financialCloseDay` of month m+1).
  const closedAnchor = useMemo(() => latestClosedMonth(financialCloseDay), [financialCloseDay]);
  const displayCloseDay = effectiveFinancialCloseDay(financialCloseDay);

  // Calendar-aligned year for each plan month in the TTM window ending at the
  // closed anchor (calendar months after the anchor come from the prior year).
  const ttmYearFor = useCallback((calendarMonth: number): number => (
    calendarMonth <= closedAnchor.month ? closedAnchor.year : closedAnchor.year - 1
  ), [closedAnchor]);

  // Default comparison month for "vs Prior Months": the latest closed month
  // per the location's close-day logic when it falls inside the window;
  // otherwise the latest window month at or before it.
  const prevMonthInfo = useMemo(() => {
    if (windowMonths.length === 0) return null;
    const anchorKey = monthKey(closedAnchor.year, closedAnchor.month);
    const anchorDate = new Date(closedAnchor.year, closedAnchor.month - 1, 1);
    return windowMonths.find(w => w.key === anchorKey)
      ?? windowMonths.filter(w => new Date(w.y, w.m - 1, 1) <= anchorDate).slice(-1)[0]
      ?? windowMonths[windowMonths.length - 1];
  }, [windowMonths, closedAnchor]);

  const prevMonthIsAdjacent = prevMonthInfo?.key === windowMonths[windowMonths.length - 1]?.key;

  // Always use latest hydrated data from accountsById so panel reflects post-mutation state
  const panelAccount = panelAccountId !== null ? (accountsById.get(panelAccountId) ?? null) : null;

  const handleDriverChange = async (
    driverType: DriverType,
    input: number | null,
    selectedDepartments?: string[],
    clearOverrides?: boolean,
    fallbackDriverType?: 'Budget' | 'LY Same Month' | null,
  ) => {
    if (!panelAccount) return;
    await updateDriver(panelAccount.chartOfAccountId, driverType, input, selectedDepartments, clearOverrides, fallbackDriverType);
    queryClient.invalidateQueries({ queryKey: ['forecast-v3', 'risk-calculate'] });
  };

  const handleOverrideChange = async (monthIndex: number, value: number | null) => {
    if (!panelAccount) return;
    if (value !== null) {
      await saveValue({
        chartOfAccountId: panelAccount.chartOfAccountId,
        year: year!,
        month: monthIndex + 1,
        forecastAmount: value,
      });
    } else {
      await clearMonthForecast(panelAccount.chartOfAccountId, year!, monthIndex + 1);
    }
    queryClient.invalidateQueries({ queryKey: ['forecast-v3', 'risk-calculate'] });
  };

  // ---- Fetch account metadata (names, types, departments) ----
  const { data: pnlRecords, isLoading: isPnlLoading } = useQuery({
    queryKey: [...forecastQueryKeys.pnlData(orgId!, locationId!, year!), 'metadata'],
    queryFn: () => fetchForecastPnLData({ orgId: orgId!, locationId: locationId!, year: year!, metadataOnly: true }),
    enabled: !!orgId && !!locationId && !!year,
    staleTime: 30000,
  });

  // Deduplicate accounts from PnL records (one entry per account)
  const accountMeta = useMemo(() => {
    const map = new Map<number, { chartOfAccountId: number; accountName: string; accountNumber: string; department: string; accountType: string }>();
    if (!pnlRecords) return map;
    for (const r of pnlRecords) {
      if (!map.has(r.chartOfAccountId)) {
        map.set(r.chartOfAccountId, {
          chartOfAccountId: r.chartOfAccountId,
          accountName: r.accountName,
          accountNumber: r.accountNumber,
          department: r.department,
          accountType: r.accountType,
        });
      }
    }
    return map;
  }, [pnlRecords]);

  const accountIds = useMemo(() => Array.from(accountMeta.keys()), [accountMeta]);

  // ---- Fetch calculated plan values for selected month (single-month scopes only) ----
  const calcMonthKey = monthKey(year!, selectedMonth);
  const roomStatSource = currentVersion?.roomStatSource ?? 'pms';
  const { data: calcResponse, isLoading: isCalcLoading } = useQuery({
    queryKey: ['forecast-v3', 'risk-calculate', { orgId, locationId, versionId, year, month: selectedMonth, roomStatSource }],
    queryFn: () => calculateForecast({
      orgId: orgId!,
      locationId: locationId!,
      versionId: versionId!,
      chartOfAccountIds: accountIds,
      months: [calcMonthKey],
      roomStatSource,
    }),
    enabled: enabled && accountIds.length > 0 && !isYearScope,
    staleTime: 30000,
  });

  // ---- Fetch TTM stats + monthly actuals + LY budget from forecastRisk ----
  const { data: riskData, isLoading: isHistoricalLoading } = useQuery({
    queryKey: ['forecast-v3', 'risk-historical', { orgId, locationId, year, month: selectedMonth }],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.FORECAST_RISK, {
        params: { orgId, locationId, year, month: selectedMonth },
      });
      return response.data as {
        accounts: RiskAccountRecord[];
        monthlyActuals: RiskMonthlyRecord[];
        lyBudgetMonthly: RiskLyBudgetRecord[];
      };
    },
    enabled: !!orgId && !!locationId && !!year,
    staleTime: 30000,
  });

  // ---- Index calculated results by accountId ----
  const calcByAccount = useMemo(() => {
    const map = new Map<number, CalculationResult>();
    if (!calcResponse?.results) return map;
    for (const r of calcResponse.results) {
      map.set(r.chartOfAccountId, r);
    }
    return map;
  }, [calcResponse]);

  // ---- Index risk records by accountId ----
  const historicalByAccount = useMemo(() => {
    const map = new Map<number, RiskAccountRecord>();
    if (!riskData?.accounts) return map;
    for (const r of riskData.accounts) {
      map.set(r.chartOfAccountId, r);
    }
    return map;
  }, [riskData]);

  // ---- Index monthly actuals: accountId → (yyyy-mm → raw amount) ----
  const monthlyByAccount = useMemo(() => {
    const map = new Map<number, Map<string, number>>();
    if (!riskData?.monthlyActuals) return map;
    for (const r of riskData.monthlyActuals) {
      let inner = map.get(r.chartOfAccountId);
      if (!inner) {
        inner = new Map<string, number>();
        map.set(r.chartOfAccountId, inner);
      }
      inner.set(monthKey(r.yr, r.mo), r.monthlyAmount);
    }
    return map;
  }, [riskData]);

  // ---- Index LY budget: accountId → (month number → raw amount) ----
  const lyBudgetByAccount = useMemo(() => {
    const map = new Map<number, Map<number, number>>();
    if (!riskData?.lyBudgetMonthly) return map;
    for (const r of riskData.lyBudgetMonthly) {
      let inner = map.get(r.chartOfAccountId);
      if (!inner) {
        inner = new Map<number, number>();
        map.set(r.chartOfAccountId, inner);
      }
      inner.set(r.mo, r.budgetAmount);
    }
    return map;
  }, [riskData]);

  // Months the "vs Prior Months" comparison actually uses
  const compareMonthKeys = useMemo(() => {
    if (selectedCompareMonths.length > 0) return selectedCompareMonths;
    return prevMonthInfo ? [prevMonthInfo.key] : [];
  }, [selectedCompareMonths, prevMonthInfo]);

  // ---- Compute risk rows ----
  const allRows = useMemo((): RiskRow[] => {
    return Array.from(accountMeta.values()).map(meta => {
      const calc = calcByAccount.get(meta.chartOfAccountId);
      const hist = historicalByAccount.get(meta.chartOfAccountId);
      const monthMap = monthlyByAccount.get(meta.chartOfAccountId);
      const lyMap = lyBudgetByAccount.get(meta.chartOfAccountId);

      const isRevenue = meta.accountType === 'Revenue';
      const rawMean = hist?.historicalMean ?? null;
      const rawMin  = hist?.historicalMin  ?? null;
      const rawMax  = hist?.historicalMax  ?? null;

      // Normalize raw Transaction amounts to the same display-positive scale used by the PnL grid:
      //   Revenue: stored as negative credits → flip sign (* -1)
      //   Expense: stored as positive debits normally, BUT contra-expense accounts (e.g. Sales Tax
      //            Rebates, refunds) are stored as negative credits — Math.abs() handles both cases.
      // calculateForecast already returns values in display-positive scale, so we use Math.abs()
      // there too as a safety net for any edge cases.
      const normalize = (v: number | null): number | null =>
        v === null ? null : isRevenue ? v * -1 : Math.abs(v);

      const historicalMean       = normalize(rawMean);
      const historicalStdDev     = hist?.historicalStdDev ?? null; // σ is always positive
      const historicalDataPoints = hist?.historicalDataPoints ?? null;
      // normalize() may swap min/max (e.g. Revenue: -10 → 10, -5 → 5, so former min becomes max).
      // Re-sort with Math.min/Math.max to guarantee historicalMin ≤ historicalMax.
      const rawAdjMin = normalize(rawMin);
      const rawAdjMax = normalize(rawMax);
      const historicalMin = rawAdjMin !== null && rawAdjMax !== null ? Math.min(rawAdjMin, rawAdjMax) : rawAdjMin ?? rawAdjMax;
      const historicalMax = rawAdjMin !== null && rawAdjMax !== null ? Math.max(rawAdjMin, rawAdjMax) : rawAdjMax ?? rawAdjMin;

      const baseRow = {
        ...meta,
        historicalMean, historicalStdDev, historicalMin, historicalMax, historicalDataPoints,
      };

      let comparisonValue: number | null = null;
      let varianceAmt: number | null = null;
      let variancePct: number | null = null;
      let flagStatus: FlagStatus = 'noData';

      // ── Full-year scope: plan vs LY budget, or plan vs TTM actuals ──
      if ((comparisonMode === 'lyBudget' || comparisonMode === 'ttm') && budgetScope === 'year') {
        const account = accountsById.get(meta.chartOfAccountId);
        // Current version values are already display-positive
        const monthlyCur = MONTH_KEYS.map(k => account?.monthlyValues?.[k] ?? 0);

        let monthlyLy: (number | null)[];
        if (comparisonMode === 'lyBudget') {
          // Budget rows follow the PnL grid convention (revenue already positive,
          // expense budgets may be stored negative) — Math.abs handles both uniformly.
          monthlyLy = Array.from({ length: 12 }, (_, i) => {
            const raw = lyMap?.get(i + 1);
            return raw != null ? Math.abs(raw) : null;
          });
        } else {
          // TTM actuals aligned by calendar month over the 12 closed months
          // ending at the location's close-day anchor
          monthlyLy = Array.from({ length: 12 }, (_, i) => {
            const raw = monthMap?.get(monthKey(ttmYearFor(i + 1), i + 1));
            return raw !== undefined ? normalize(raw) : null;
          });
        }
        const hasHistory = comparisonMode === 'lyBudget'
          ? !!lyMap && lyMap.size > 0
          : monthlyLy.some(v => v !== null);

        const monthlyDeltas = monthlyCur.map((cur, i) => cur - (monthlyLy[i] ?? 0));

        const curTotal = monthlyCur.reduce((s, v) => s + v, 0);
        const lyTotal = hasHistory
          ? monthlyLy.reduce<number>((s, v) => s + (v ?? 0), 0)
          : null;

        const forecast = curTotal;
        comparisonValue = lyTotal;

        if (lyTotal === null) {
          // No comparison history at all (no LY budget / no TTM actuals):
          // a material planned amount is new spend/revenue
          if (Math.abs(curTotal) >= MATERIAL_THRESHOLD) {
            varianceAmt = curTotal;
            flagStatus = 'over';
          }
        } else {
          varianceAmt = curTotal - lyTotal;
          variancePct = lyTotal !== 0 ? (varianceAmt / Math.abs(lyTotal)) * 100 : null;

          if (Math.abs(varianceAmt) >= MATERIAL_THRESHOLD &&
              (variancePct === null || Math.abs(variancePct) > threshold)) {
            flagStatus = varianceAmt > 0 ? 'over' : 'under';
          } else {
            flagStatus = 'ok';
            // FY totals can net out while individual months shift materially —
            // a big month-level delta still flags the account.
            for (let i = 0; i < 12; i++) {
              const delta = monthlyDeltas[i];
              const lyM = monthlyLy[i] ?? 0;
              if (Math.abs(delta) >= MONTH_DELTA_FLOOR &&
                  (lyM === 0 || (Math.abs(delta) / Math.abs(lyM)) * 100 > threshold)) {
                flagStatus = delta > 0 ? 'over' : 'under';
                break;
              }
            }
          }
        }

        return {
          ...baseRow, forecast, comparisonValue, varianceAmt, variancePct, flagStatus,
          monthlyCur, monthlyLy, monthlyDeltas,
        };
      }

      // ── Single-month scopes ──
      const forecast = calc ? Math.abs(calc.calculatedAmount) : null;

      if (forecast === null) {
        return { ...baseRow, forecast, comparisonValue, varianceAmt, variancePct, flagStatus };
      }

      if (comparisonMode === 'ttm') {
        if (historicalMean !== null && historicalMin !== null && historicalMax !== null) {
          comparisonValue = historicalMean;
          varianceAmt = forecast - historicalMean;
          variancePct = historicalMean !== 0
            ? (varianceAmt / Math.abs(historicalMean)) * 100
            : null;

          if (Math.abs(varianceAmt) < MATERIAL_THRESHOLD) {
            flagStatus = 'ok';
          } else if (forecast > historicalMax) {
            flagStatus = 'over';
          } else if (forecast < historicalMin) {
            flagStatus = 'under';
          } else {
            flagStatus = 'ok';
          }
        }
      } else if (comparisonMode === 'lyBudget') {
        const rawLy = lyMap?.get(selectedMonth);
        const lastYearBudget = rawLy != null ? Math.abs(rawLy) : null;
        if (lastYearBudget === null) {
          // No LY budget for this month: a material planned amount is new spend/revenue
          if (Math.abs(forecast) >= MATERIAL_THRESHOLD) {
            varianceAmt = forecast;
            flagStatus = 'over';
          }
        } else {
          comparisonValue = lastYearBudget;
          if (lastYearBudget !== 0) {
            varianceAmt = forecast - lastYearBudget;
            variancePct = (varianceAmt / Math.abs(lastYearBudget)) * 100;
            if (Math.abs(varianceAmt) < MATERIAL_THRESHOLD || Math.abs(variancePct) <= threshold) {
              flagStatus = 'ok';
            } else {
              flagStatus = variancePct > 0 ? 'over' : 'under';
            }
          } else if (Math.abs(forecast) >= MATERIAL_THRESHOLD) {
            varianceAmt = forecast;
            variancePct = null;
            flagStatus = forecast > 0 ? 'over' : 'under';
          } else {
            flagStatus = 'ok';
          }
        }
      } else if (comparisonMode === 'months') {
        // Average of the chosen months' actuals. A chosen month with no rows
        // counts as $0 activity, but if the account has no rows in ANY chosen
        // month it stays noData (avoids flagging accounts with no history).
        const rawValues = compareMonthKeys.map(k => monthMap?.get(k));
        const hasAnyData = rawValues.some(v => v !== undefined);
        if (hasAnyData && compareMonthKeys.length > 0) {
          const sum = rawValues.reduce<number>((s, v) => s + (normalize(v ?? 0) ?? 0), 0);
          const avg = sum / compareMonthKeys.length;
          comparisonValue = avg;
          varianceAmt = forecast - avg;
          variancePct = avg !== 0
            ? (varianceAmt / Math.abs(avg)) * 100
            : null;
          if (Math.abs(varianceAmt) < MATERIAL_THRESHOLD) {
            flagStatus = 'ok';
          } else if (variancePct !== null && Math.abs(variancePct) > threshold) {
            flagStatus = variancePct > 0 ? 'over' : 'under';
          } else if (variancePct === null && Math.abs(varianceAmt) >= MATERIAL_THRESHOLD) {
            flagStatus = varianceAmt > 0 ? 'over' : 'under';
          } else {
            flagStatus = 'ok';
          }
        }
      }

      return { ...baseRow, forecast, comparisonValue, varianceAmt, variancePct, flagStatus };
    });
  }, [accountMeta, calcByAccount, historicalByAccount, monthlyByAccount, lyBudgetByAccount, accountsById, compareMonthKeys, comparisonMode, budgetScope, threshold, selectedMonth, ttmYearFor]);

  // ---- Filter + sort ----
  const filteredRows = useMemo(() => {
    let rows = allRows;

    if (showFlaggedOnly) {
      rows = rows.filter(r => r.flagStatus === 'over' || r.flagStatus === 'under');
    }
    if (filterType !== 'all') {
      rows = rows.filter(r => r.accountType === filterType);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter(r =>
        r.accountName.toLowerCase().includes(q) ||
        r.accountNumber.toLowerCase().includes(q) ||
        r.department.toLowerCase().includes(q)
      );
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let aVal: string | number | null;
      let bVal: string | number | null;
      switch (sortField) {
        case 'accountName': aVal = a.accountName; bVal = b.accountName; break;
        case 'department':  aVal = a.department;  bVal = b.department;  break;
        case 'forecast':    aVal = a.forecast;    bVal = b.forecast;    break;
        case 'comparison':  aVal = a.comparisonValue; bVal = b.comparisonValue; break;
        case 'varianceAmt': aVal = a.varianceAmt !== null ? Math.abs(a.varianceAmt) : -Infinity;
                            bVal = b.varianceAmt !== null ? Math.abs(b.varianceAmt) : -Infinity; break;
        case 'variancePct': aVal = a.variancePct !== null ? Math.abs(a.variancePct) : -Infinity;
                            bVal = b.variancePct !== null ? Math.abs(b.variancePct) : -Infinity; break;
        default: return 0;
      }
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * dir;
      }
      return ((aVal as number) - (bVal as number)) * dir;
    });
  }, [allRows, showFlaggedOnly, filterType, searchTerm, sortField, sortDir]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  }, [sortField]);

  const flagCounts = useMemo(() => ({
    over:   allRows.filter(r => r.flagStatus === 'over').length,
    under:  allRows.filter(r => r.flagStatus === 'under').length,
    ok:     allRows.filter(r => r.flagStatus === 'ok').length,
    noData: allRows.filter(r => r.flagStatus === 'noData').length,
  }), [allRows]);

  const isLoading = isPnlLoading || isHistoricalLoading || (isYearScope ? isAccountDataLoading : isCalcLoading);

  const modeLabels: Record<ComparisonMode, string> = {
    ttm: 'TTM',
    lyBudget: year ? `vs ${year - 1} Budget` : 'vs LY Budget',
    months: 'vs Prior Months',
  };

  const comparisonLabel = useMemo(() => {
    if (comparisonMode === 'ttm') return 'TTM Avg';
    if (comparisonMode === 'lyBudget') return year ? `${year - 1} Budget` : 'LY Budget';
    if (selectedCompareMonths.length === 0) return prevMonthInfo ? `${prevMonthInfo.label} Actual` : 'Last Month';
    if (selectedCompareMonths.length === 1) {
      const info = windowMonths.find(m => m.key === selectedCompareMonths[0]);
      return info ? `${info.label} Actual` : 'Selected Month';
    }
    return `Avg of ${selectedCompareMonths.length} mo`;
  }, [comparisonMode, year, selectedCompareMonths, prevMonthInfo, windowMonths]);

  const toggleCompareMonth = useCallback((key: string) => {
    setSelectedCompareMonths(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  }, []);

  // Color for a full-year month delta cell (matches variance color semantics)
  const deltaCellColor = useCallback((delta: number, lyM: number | null): string => {
    if (Math.abs(delta) < MATERIAL_THRESHOLD) return 'var(--lodge-onyx-300)';
    const base = lyM ?? 0;
    const exceedsPct = base === 0 || (Math.abs(delta) / Math.abs(base)) * 100 > threshold;
    if (!exceedsPct) return 'var(--lodge-onyx-500)';
    return delta > 0 ? '#dc2626' : 'var(--fv3-accent-ink, #d97706)';
  }, [threshold]);

  if (!versionId) {
    return (
      <div className="flex flex-1 items-center justify-center" style={{ color: 'var(--lodge-onyx-400)' }}>
        <div className="flex flex-col items-center gap-3">
          <ShieldAlert size={32} style={{ opacity: 0.4 }} />
          <p className="text-sm">No active version selected. Open a version in Workspace to use Risk.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 overflow-hidden" style={{ background: 'var(--lodge-white)' }}>
    <div className="flex flex-col flex-1 min-w-0 overflow-hidden">

      {/* ── Controls bar ── */}
      <div
        className="flex flex-wrap items-center gap-3 px-4 py-3 border-b flex-shrink-0"
        style={{ borderColor: 'var(--lodge-onyx-100)', background: 'var(--lodge-onyx-50)' }}
      >
        {/* Comparison mode toggle */}
        <div
          className="flex rounded-md overflow-hidden border"
          style={{ borderColor: 'var(--lodge-onyx-200)' }}
        >
          {(['ttm', 'lyBudget', 'months'] as ComparisonMode[]).map(mode => {
            const active = comparisonMode === mode;
            return (
              <button
                key={mode}
                onClick={() => setComparisonMode(mode)}
                className="px-3 py-1.5 text-xs font-medium transition-colors"
                style={active
                  ? { background: 'var(--lodge-orange-400)', color: 'white' }
                  : { background: 'var(--lodge-white)', color: 'var(--lodge-onyx-500)' }
                }
              >
                {modeLabels[mode]}
              </button>
            );
          })}
        </div>

        {/* Scope toggle (TTM + LY budget modes): single month vs full year */}
        {(comparisonMode === 'lyBudget' || comparisonMode === 'ttm') && (
          <div
            className="flex rounded-md overflow-hidden border"
            style={{ borderColor: 'var(--lodge-onyx-200)' }}
          >
            {(['month', 'year'] as BudgetScope[]).map(scope => {
              const active = budgetScope === scope;
              return (
                <button
                  key={scope}
                  onClick={() => setBudgetScope(scope)}
                  className="px-3 py-1.5 text-xs font-medium transition-colors"
                  style={active
                    ? { background: 'var(--lodge-orange-400)', color: 'white' }
                    : { background: 'var(--lodge-white)', color: 'var(--lodge-onyx-500)' }
                  }
                >
                  {scope === 'month' ? SHORT_MONTH_NAMES[selectedMonth - 1] : 'Full Year'}
                </button>
              );
            })}
          </div>
        )}

        {/* Month multi-select (months mode only) */}
        {comparisonMode === 'months' && (
          <div ref={monthDropdownRef} className="relative">
            <button
              onClick={() => setShowMonthDropdown(v => !v)}
              className="flex items-center gap-1.5 text-xs font-medium border rounded px-2 py-1.5 transition-colors"
              style={showMonthDropdown
                ? { borderColor: 'var(--lodge-orange-300)', background: 'var(--lodge-orange-50)', color: 'var(--lodge-onyx-700)' }
                : { borderColor: 'var(--lodge-onyx-200)', background: 'var(--lodge-white)', color: 'var(--lodge-onyx-600)' }}
            >
              {selectedCompareMonths.length === 0
                ? `${prevMonthIsAdjacent ? 'Last month' : 'Latest actuals'}${prevMonthInfo ? ` (${prevMonthInfo.label})` : ''}`
                : selectedCompareMonths.length === 1
                  ? (windowMonths.find(m => m.key === selectedCompareMonths[0])?.label ?? '1 month')
                  : `${selectedCompareMonths.length} months selected`}
              <ChevronDown size={12} />
            </button>
            {showMonthDropdown && (
              <div
                className="absolute top-full mt-1 left-0 rounded-lg z-50 p-2 min-w-[200px]"
                style={{ background: 'var(--lodge-white)', border: '1px solid var(--lodge-onyx-100)', boxShadow: 'var(--shadow-lg)' }}
              >
                <div className="px-1 pb-1.5 text-[11px] font-medium" style={{ color: 'var(--lodge-onyx-400)' }}>
                  Compare against the average of:
                </div>
                <div className="max-h-64 overflow-auto">
                  {[...windowMonths].reverse().map(m => (
                    <label
                      key={m.key}
                      className="flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer text-xs"
                      style={{ color: 'var(--lodge-onyx-700)' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCompareMonths.includes(m.key)}
                        onChange={() => toggleCompareMonth(m.key)}
                        className="rounded border-slate-300"
                        style={{ accentColor: 'var(--lodge-orange-400)' }}
                      />
                      {m.label}
                    </label>
                  ))}
                </div>
                {selectedCompareMonths.length > 0 && (
                  <button
                    onClick={() => setSelectedCompareMonths([])}
                    className="mt-1.5 w-full text-xs py-1 rounded transition-colors"
                    style={{ color: 'var(--lodge-orange-500)', borderTop: '1px solid var(--lodge-onyx-50)' }}
                  >
                    Reset to {prevMonthIsAdjacent ? 'last month' : 'latest actuals'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Threshold — %-based comparisons (everything except the single-month TTM range) */}
        {(comparisonMode !== 'ttm' || isYearScope) && (
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-medium" style={{ color: 'var(--lodge-onyx-500)' }}>Flag if ±</label>
            <input
              type="number"
              min={0}
              max={100}
              value={threshold}
              onChange={e => setThreshold(Math.max(0, Math.min(100, Number(e.target.value))))}
              className="w-14 text-sm border rounded px-2 py-1 text-center"
              style={{ borderColor: 'var(--lodge-onyx-200)', color: 'var(--lodge-onyx-800)', background: 'var(--lodge-white)' }}
            />
            <span className="text-xs" style={{ color: 'var(--lodge-onyx-500)' }}>%</span>
          </div>
        )}

        {/* Show flagged only toggle */}
        <label className="flex items-center gap-2 cursor-pointer ml-1">
          <div
            className="relative inline-flex items-center"
            onClick={() => setShowFlaggedOnly(v => !v)}
          >
            <div
              className="w-8 h-4 rounded-full transition-colors"
              style={{ background: showFlaggedOnly ? 'var(--lodge-orange-400)' : 'var(--lodge-onyx-200)' }}
            />
            <div
              className="absolute w-3 h-3 bg-white rounded-full shadow transition-transform"
              style={{ transform: showFlaggedOnly ? 'translateX(17px)' : 'translateX(2px)' }}
            />
          </div>
          <span className="text-xs font-medium" style={{ color: 'var(--lodge-onyx-600)' }}>Flagged only</span>
        </label>

        {/* Search + type filter — pushed right */}
        <div className="flex items-center gap-2 ml-auto">
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value as typeof filterType)}
            className="text-xs border rounded px-2 py-1"
            style={{ borderColor: 'var(--lodge-onyx-200)', color: 'var(--lodge-onyx-600)', background: 'var(--lodge-white)' }}
          >
            <option value="all">All types</option>
            <option value="Revenue">Revenue</option>
            <option value="Expense">Expense</option>
          </select>
          <input
            type="text"
            placeholder="Search accounts…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="text-xs border rounded px-2 py-1 w-40"
            style={{ borderColor: 'var(--lodge-onyx-200)', color: 'var(--lodge-onyx-800)', background: 'var(--lodge-white)' }}
          />
        </div>
      </div>

      {/* ── Summary chips ── */}
      {!isLoading && (
        <div className="flex items-center gap-3 px-4 py-2 border-b flex-shrink-0" style={{ borderColor: 'var(--lodge-onyx-100)' }}>
          <span className="text-xs" style={{ color: 'var(--lodge-onyx-400)' }}>
            {isYearScope ? `Full Year ${year}` : `${MONTH_NAMES[selectedMonth - 1]} ${year}`}
          </span>
          <span style={{ color: 'var(--lodge-onyx-200)' }}>·</span>
          {flagCounts.over > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: '#dc2626' }}>
              <TrendingUp size={12} /> {flagCounts.over} over
            </span>
          )}
          {flagCounts.under > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: 'var(--fv3-accent-ink, #d97706)' }}>
              <TrendingDown size={12} /> {flagCounts.under} under
            </span>
          )}
          {flagCounts.ok > 0 && (
            <span className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--lodge-onyx-400)' }}>
              <CheckCircle2 size={12} /> {flagCounts.ok} within range
            </span>
          )}
          {flagCounts.noData > 0 && (
            <span className="inline-flex items-center gap-1 text-xs" style={{ color: 'var(--lodge-onyx-400)' }}>
              <HelpCircle size={12} /> {flagCounts.noData} no {kindLower}
            </span>
          )}
          <span className="ml-auto text-xs" style={{ color: 'var(--lodge-onyx-400)' }}>
            {filteredRows.length} {showFlaggedOnly ? 'flagged' : 'accounts'} shown
          </span>
        </div>
      )}

      {/* ── Table ── */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex flex-col gap-2 p-4">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="sk-pulse h-8 rounded animate-pulse"
                style={{ background: 'var(--lodge-onyx-100)', width: `${70 + (i % 3) * 10}%` }}
              />
            ))}
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-2" style={{ color: 'var(--lodge-onyx-400)' }}>
            <CheckCircle2 size={28} style={{ color: '#16a34a', opacity: 0.7 }} />
            <p className="text-sm font-medium">
              {showFlaggedOnly
                ? `No flagged accounts for ${isYearScope ? year : `${MONTH_NAMES[selectedMonth - 1]} ${year}`}`
                : 'No accounts match your filters'}
            </p>
          </div>
        ) : (
          <table className="text-sm border-collapse" style={{ width: '100%', minWidth: isYearScope ? 1750 : undefined }}>
            <thead>
              <tr style={{ background: 'var(--lodge-onyx-50)', borderBottom: '1px solid var(--lodge-onyx-100)' }}>
                <SortHeader field="accountName" label="Account" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortHeader field="department" label="Department" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                {!isYearScope && (
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-left" style={{ color: 'var(--lodge-onyx-400)' }}>
                    Type
                  </th>
                )}
                <SortHeader
                  field="forecast"
                  label={isYearScope ? `FY ${year} ${kindLabel}` : kindLabel}
                  sortField={sortField} sortDir={sortDir} onSort={handleSort} align="right"
                />
                <SortHeader
                  field="comparison"
                  label={isYearScope
                    ? (comparisonMode === 'ttm' ? 'TTM Actuals' : `FY ${year! - 1} Budget`)
                    : comparisonLabel}
                  sortField={sortField} sortDir={sortDir} onSort={handleSort} align="right"
                />
                {comparisonMode === 'ttm' && !isYearScope && (
                  <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-right" style={{ color: 'var(--lodge-onyx-400)' }}>
                    Range (min–max)
                  </th>
                )}
                <SortHeader field="varianceAmt" label="Var $" sortField={sortField} sortDir={sortDir} onSort={handleSort} align="right" />
                <SortHeader field="variancePct" label="Var %" sortField={sortField} sortDir={sortDir} onSort={handleSort} align="right" />
                {isYearScope && SHORT_MONTH_NAMES.map((m, i) => {
                  const isTtm = comparisonMode === 'ttm';
                  const compareYear = ttmYearFor(i + 1);
                  const isAnchor = isTtm && i + 1 === closedAnchor.month && compareYear === closedAnchor.year;
                  return (
                    <th
                      key={m}
                      className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-right whitespace-nowrap"
                      style={{ color: isAnchor ? 'var(--lodge-orange-500)' : 'var(--lodge-onyx-400)' }}
                      title={isAnchor ? `Most recent closed month (location close day: ${displayCloseDay})` : undefined}
                    >
                      {isTtm ? `${m} '${String(compareYear % 100).padStart(2, '0')}` : m}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, idx) => {
                const isFlagged = row.flagStatus === 'over' || row.flagStatus === 'under';
                const isSelected = panelAccountId === row.chartOfAccountId;
                return (
                  <tr
                    key={row.chartOfAccountId}
                    onClick={() => setPanelAccountId(isSelected ? null : row.chartOfAccountId)}
                    style={{
                      cursor: 'pointer',
                      background: isSelected
                        ? 'var(--lodge-orange-50, var(--fv3-accent-wash, #fff7ed))'
                        : isFlagged
                          ? (row.flagStatus === 'over' ? 'rgba(254,226,226,0.25)' : 'rgba(254,243,199,0.25)')
                          : (idx % 2 === 0 ? 'var(--lodge-white)' : 'var(--lodge-onyx-50)'),
                      borderBottom: '1px solid var(--lodge-onyx-100)',
                      outline: isSelected ? '2px solid var(--lodge-orange-300, var(--fv3-chart-secondary, #fdba74))' : undefined,
                      outlineOffset: isSelected ? '-2px' : undefined,
                    }}
                  >
                    {/* Account */}
                    <td className="px-3 py-2">
                      <div className="font-medium whitespace-nowrap" style={{ color: 'var(--lodge-onyx-800)' }}>{row.accountName}</div>
                      <div className="text-xs" style={{ color: 'var(--lodge-onyx-400)' }}>{row.accountNumber}</div>
                    </td>

                    {/* Department */}
                    <td className="px-3 py-2 text-xs" style={{ color: 'var(--lodge-onyx-500)' }}>
                      {row.department || '—'}
                    </td>

                    {/* Type — single-month scopes only (year scope needs the width for months) */}
                    {!isYearScope && (
                      <td className="px-3 py-2">
                        <span
                          className="text-xs px-1.5 py-0.5 rounded"
                          style={row.accountType === 'Revenue'
                            ? { background: '#dcfce7', color: '#15803d' }
                            : { background: 'var(--fv3-accent-wash, #fef3c7)', color: 'var(--fv3-accent-ink, #b45309)' }
                          }
                        >
                          {row.accountType}
                        </span>
                      </td>
                    )}

                    {/* Plan value (forecast or budget) */}
                    <td className="px-3 py-2 text-right font-medium tabular-nums" style={{ color: 'var(--lodge-onyx-800)' }}>
                      {fmtCurrency(row.forecast)}
                    </td>

                    {/* Comparison value */}
                    <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--lodge-onyx-500)' }}>
                      {fmtCurrency(row.comparisonValue)}
                      {comparisonMode === 'ttm' && !isYearScope && row.historicalDataPoints !== null && (
                        <div className="text-xs" style={{ color: 'var(--lodge-onyx-300)' }}>
                          {row.historicalDataPoints} mo
                        </div>
                      )}
                    </td>

                    {/* TTM range — single-month TTM only */}
                    {comparisonMode === 'ttm' && !isYearScope && (
                      <td className="px-3 py-2 text-right text-xs tabular-nums" style={{ color: 'var(--lodge-onyx-400)' }}>
                        {row.historicalMin !== null && row.historicalMax !== null
                          ? `${fmtCurrency(row.historicalMin)} – ${fmtCurrency(row.historicalMax)}`
                          : '—'}
                      </td>
                    )}

                    {/* Variance $ */}
                    <td
                      className="px-3 py-2 text-right font-medium tabular-nums"
                      style={{ color: row.varianceAmt === null ? 'var(--lodge-onyx-300)' : row.varianceAmt > 0 ? '#dc2626' : 'var(--fv3-accent-ink, #d97706)' }}
                    >
                      {fmtCurrency(row.varianceAmt)}
                    </td>

                    {/* Variance % */}
                    <td
                      className="px-3 py-2 text-right font-medium tabular-nums"
                      style={{ color: row.variancePct === null ? 'var(--lodge-onyx-300)' : Math.abs(row.variancePct) > threshold ? (row.variancePct > 0 ? '#dc2626' : 'var(--fv3-accent-ink, #d97706)') : 'var(--lodge-onyx-600)' }}
                    >
                      {fmtPct(row.variancePct)}
                    </td>

                    {/* Full-year scope: month-by-month deltas */}
                    {isYearScope && SHORT_MONTH_NAMES.map((m, i) => {
                      const delta = row.monthlyDeltas?.[i] ?? 0;
                      const cur = row.monthlyCur?.[i] ?? 0;
                      const ly = row.monthlyLy?.[i] ?? null;
                      const compareDesc = comparisonMode === 'ttm'
                        ? `${m} ${ttmYearFor(i + 1)} actual`
                        : `${m} ${year! - 1} budget`;
                      return (
                        <td
                          key={m}
                          className="px-2 py-2 text-right text-xs tabular-nums whitespace-nowrap"
                          style={{ color: deltaCellColor(delta, ly) }}
                          title={`${m} ${year} ${kindLower} ${fmtCurrency(cur)} vs ${compareDesc} ${fmtCurrency(ly)}`}
                        >
                          {Math.abs(delta) < MATERIAL_THRESHOLD ? '—' : fmtDelta(delta)}
                        </td>
                      );
                    })}

                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Footer note ── */}
      {!isLoading && comparisonMode === 'ttm' && !isYearScope && (
        <div className="px-4 py-2 border-t text-xs flex-shrink-0" style={{ borderColor: 'var(--lodge-onyx-100)', color: 'var(--lodge-onyx-400)' }}>
          TTM uses the 12 months of actuals immediately before the selected month. The range (min–max) reflects the spread across those monthly actuals.
        </div>
      )}
      {!isLoading && comparisonMode === 'ttm' && isYearScope && year && (
        <div className="px-4 py-2 border-t text-xs flex-shrink-0" style={{ borderColor: 'var(--lodge-onyx-100)', color: 'var(--lodge-onyx-400)' }}>
          Comparing the full-year {year} {kindLower} against the trailing 12 closed months of actuals — through {SHORT_MONTH_NAMES[closedAnchor.month - 1]} {closedAnchor.year} per this location's close day ({displayCloseDay}) — aligned by calendar month. Accounts also flag when any single month shifts more than {threshold}% (and at least {formatCurrencyRounded(MONTH_DELTA_FLOOR)}). Accounts with no actuals in the window but a planned amount are flagged as new.
        </div>
      )}
      {!isLoading && comparisonMode === 'lyBudget' && year && (
        <div className="px-4 py-2 border-t text-xs flex-shrink-0" style={{ borderColor: 'var(--lodge-onyx-100)', color: 'var(--lodge-onyx-400)' }}>
          {isYearScope
            ? `Comparing the full-year ${year} ${kindLower} against the ${year - 1} budget, month by month. Deltas are ${year} minus ${year - 1}; accounts also flag when any single month shifts more than ${threshold}% (and at least ${formatCurrencyRounded(MONTH_DELTA_FLOOR)}). Accounts with no ${year - 1} budget but a planned amount are flagged as new.`
            : `Comparing this ${kindLower} against the ${year - 1} budget for ${MONTH_NAMES[selectedMonth - 1]}. Accounts with no ${year - 1} budget but a planned amount are flagged as new.`}
        </div>
      )}
      {!isLoading && comparisonMode === 'months' && selectedCompareMonths.length > 1 && (
        <div className="px-4 py-2 border-t text-xs flex-shrink-0" style={{ borderColor: 'var(--lodge-onyx-100)', color: 'var(--lodge-onyx-400)' }}>
          Comparing against the average of: {windowMonths.filter(m => selectedCompareMonths.includes(m.key)).map(m => m.label).join(', ')}
        </div>
      )}
    </div>

    {/* ── Account Detail Panel ── */}
    {panelAccount && (
      <AccountDetailPanel
        account={panelAccount}
        onClose={() => setPanelAccountId(null)}
        onDriverChange={handleDriverChange}
        onOverrideChange={handleOverrideChange}
        versionId={currentVersion?.id}
        locationId={locationId ?? undefined}
        selectedYear={year ?? undefined}
        isRecalculating={isPanelRefetching}
      />
    )}
    </div>
  );
}

export default RiskPage;
